import { useCallback, useEffect, useState } from "react";
import { Bell, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loading, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

export const NotificationSettings = () => {
  const [meta, setMeta] = useState(null);
  const [prefs, setPrefs] = useState({});
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get("/me/notification-preferences");
    setMeta(res.data);
    setPrefs(res.data.prefs);
    setPhone(res.data.phone || "");
  }, []);

  useEffect(() => { load().catch(() => setMeta(false)); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/notification-preferences", { ...prefs, phone });
      toast.success("Your notification choices are saved");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (!meta) return <Loading label="Loading your notification choices" />;

  return (
    <Panel testId="panel-notifications" kicker="Yours alone"
           title="How you want to hear about approvals"
           actions={(
             <Button size="sm" onClick={save} disabled={busy} data-testid="notify-save-button"
                     className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
               {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                 : <Save className="mr-1 h-3.5 w-3.5" />} Save choices
             </Button>
           )}>
      <div className="space-y-3 p-4 pt-0">
        <p className="text-xs text-muted-foreground">
          When an approval needs you — an overspend, an extra funds request, a conflicted grant or a
          supplier interest — we tell you on the channels you tick here. This choice is yours only;
          every office bearer and trustee sets their own.
        </p>
        {meta.channels.map((c) => (
          <div key={c.key} className="flex items-center justify-between rounded-xl border border-border bg-muted/30 p-3">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              <div>
                <div className="text-sm font-medium text-foreground">{c.label}</div>
                <div className="text-[11px] text-muted-foreground">{c.help}</div>
              </div>
            </div>
            <Switch data-testid={`notify-switch-${c.key}`} checked={!!prefs[c.key]}
                    onCheckedChange={(v) => setPrefs({ ...prefs, [c.key]: v })} />
          </div>
        ))}
        <div className="space-y-1.5">
          <div className="text-[11px] text-muted-foreground">Mobile number for WhatsApp</div>
          <Input data-testid="notify-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                 placeholder="+91 98200 00000" className="max-w-xs bg-muted/40" />
        </div>
        <div className="text-[11px] text-muted-foreground" data-testid="notify-delivery-status">
          Delivery today: email {meta.delivery.email ? "connected" : "not configured"} · WhatsApp{" "}
          {meta.delivery.whatsapp ? "connected" : "not configured"}. Portal alerts always work; email
          and WhatsApp start sending as soon as the Platform Master Admin adds the provider keys.
        </div>
      </div>
    </Panel>
  );
};
